/**
 * Avisos de sistema ao CLIENTE nas transições de interlocutor — Item 480 (achado A-27 da
 * noturna de 04/Set/2026). Sem eles, no canal `simulador` a última fala que o cliente via
 * era a do decoy e depois vinha só a fala manual do operador: ninguém dizia que um humano
 * assumiu, retomou da IA ou encerrou.
 *
 * Lógica PURA compartilhada BE ↔ FE (mesma disciplina de `resolveEscalationLimboPolicy`):
 * o editor da policy (ConductTab, web) mostra EXATAMENTE o que o runtime fará
 * (`CustomerNoticesListener`, engine). Neutra de vertical: os textos default não citam
 * empresa, produto nem setor — o tenant edita.
 *
 * Decisões do TL (04/Set/2026): «assumiu» e «encerrou» SIM; «retomou» só quando a IA já
 * falou depois do humano (regra do listener, não desta policy); tudo config do tenant,
 * ligado por padrão, textos editáveis.
 */
export const CUSTOMER_NOTICE_KINDS = ['humanAssumed', 'humanResumed', 'closed'];
/** Teto do texto (o mesmo do Zod no engine, `tenant-config.schema.ts`). */
export const CUSTOMER_NOTICE_TEXT_MAX = 300;
export const CUSTOMER_NOTICES_DEFAULTS = {
    humanAssumed: { enabled: true, text: 'Você está falando com {nome} a partir de agora.' },
    humanResumed: { enabled: true, text: '{nome} voltou a atender você a partir de agora.' },
    closed: {
        enabled: true,
        text: 'Este atendimento foi encerrado. Se precisar, é só escrever de novo.',
    },
};
/** Rótulos PT para a tela (o enum cru nunca chega ao gestor). */
export const CUSTOMER_NOTICE_LABELS = {
    humanAssumed: {
        title: 'Quando uma pessoa assume a conversa',
        hint: 'Sai quando um atendente assume uma conversa que estava na fila ou é atribuído a ela.',
    },
    humanResumed: {
        title: 'Quando uma pessoa retoma da IA',
        hint: 'Sai quando um atendente retoma uma conversa que a IA conduzia — e só se a IA falou depois do último humano.',
    },
    closed: {
        title: 'Quando a conversa é encerrada',
        hint: 'Sai quando o atendimento é encerrado (resolvido ou não). Reabertura pelo cliente não avisa.',
    },
};
function resolveSetting(raw, fallback) {
    const obj = typeof raw === 'object' && raw !== null && !Array.isArray(raw)
        ? raw
        : {};
    // Só `false` literal desliga — o default do TL é ligado.
    const enabled = obj.enabled !== false;
    const text = typeof obj.text === 'string' && obj.text.trim().length > 0
        ? obj.text.trim().slice(0, CUSTOMER_NOTICE_TEXT_MAX)
        : fallback.text;
    return { enabled, text };
}
/**
 * Parse defensivo do `Tenant.customerNoticesPolicy` (Json cru). `null`/malformado ⇒ defaults.
 * A validação estrita do PATCH vive no engine (`tenant-config.schema.ts`).
 */
export function resolveCustomerNoticesPolicy(raw) {
    const obj = typeof raw === 'object' && raw !== null && !Array.isArray(raw)
        ? raw
        : {};
    return {
        humanAssumed: resolveSetting(obj.humanAssumed, CUSTOMER_NOTICES_DEFAULTS.humanAssumed),
        humanResumed: resolveSetting(obj.humanResumed, CUSTOMER_NOTICES_DEFAULTS.humanResumed),
        closed: resolveSetting(obj.closed, CUSTOMER_NOTICES_DEFAULTS.closed),
    };
}
/**
 * Substitui `{nome}`. Sem nome (usuário sem `name`, ou não resolvido) cai em «um atendente»
 * — nunca deixa o placeholder nem um buraco na frase.
 */
export function renderCustomerNotice(text, vars) {
    const name = vars.name !== undefined && vars.name !== null && vars.name.trim().length > 0
        ? vars.name.trim()
        : 'um atendente';
    return text.replace(/\{nome\}/g, name);
}
//# sourceMappingURL=index.js.map